import React, { useEffect, useState } from 'react'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import useDeemedReportStyles from './styles'
import {
  IconButton,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import { Delete, Clear, Edit } from '@material-ui/icons'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers'
import moment from 'moment'
import { DeleteLogDta } from '../../../../services/api'
import CustomAlert from '../../../../components/CustomAlert/CustomAlert'

const DeemedReport = ({
  DeemedlogData,
  token,
  state,
  refreshLogs,
  onEditLog,
}) => {
  console.log('DeemedlogData', DeemedlogData, token, refreshLogs, onEditLog)

  const classes = useDeemedReportStyles()

  const [selectedDate, setSelectedDate] = useState(null)
  const [filteredLogs, setFilteredLogs] = useState([])

  // Alert state
  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [alertSeverity, setAlertSeverity] = useState('success')

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [logToDelete, setLogToDelete] = useState(null)

  const formatTime = (timeString) => {
    if (timeString && timeString.match(/^\d{1,2}:\d{2}$/)) {
      // Convert to 12-hour format
      const [hours, minutes] = timeString.split(':')
      let hour24 = parseInt(hours)

      // Handle midnight case: if hour is 0, display as 12 AM
      if (hour24 === 0) {
        return `12:${minutes} AM`
      }

      const hour12 = hour24 > 12 ? hour24 - 12 : hour24
      const ampm = hour24 >= 12 ? 'PM' : 'AM'

      // Handle noon case: 12 PM
      if (hour24 === 12) {
        return `12:${minutes} PM`
      }

      return `${hour12}:${minutes} ${ampm}`
    }

    // for legacy method
    try {
      const date = new Date(timeString)
      if (isNaN(date.getTime())) {
        return timeString
      }
      return date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    } catch (error) {
      return timeString
    }
  }

  const formatLogDate = (logDate) => {
    const date = new Date(logDate)
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0') // Months are 0-indexed
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  // Helper function to parse time string to minutes
  const parseTime = (timeString) => {
    const timeMatch = timeString.match(/^(\d{1,2}):(\d{2})$/)
    if (timeMatch) {
      let hours = parseInt(timeMatch[1])
      const minutes = parseInt(timeMatch[2])

      // Handle 24-hour format properly
      // If hours is 24, convert to 0 (midnight next day)
      if (hours === 24) {
        hours = 0
      }

      return hours * 60 + minutes
    }
    return null
  }

  // Function to calculate total duration in minutes
  const calculateTotalDuration = (durationString) => {
    if (!durationString) return 0

    const periods = durationString.includes(' | ')
      ? durationString.split(' | ')
      : [durationString]

    let totalMinutes = 0

    periods.forEach((period) => {
      const [startTime, endTime] = period.split(' : ')
      if (startTime && endTime) {
        const start = parseTime(startTime.trim())
        const end = parseTime(endTime.trim())

        if (start !== null && end !== null) {
          let duration = end - start

          // Handle crossing midnight or same time edge cases
          if (duration < 0) {
            // Handle crossing midnight
            duration += 24 * 60
          } else if (duration === 0) {
            // If start and end are the same, check if it's actually a full day
            // or if there's an error in the data
            // For now, we'll treat same times as 0 duration
            duration = 0
          }

          totalMinutes += duration
        }
      }
    })

    return totalMinutes
  }

  // Function to format minutes to readable duration
  const formatDuration = (minutes) => {
    if (minutes === 0) {
      return '0 minutes'
    }

    if (minutes < 60) {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`
    } else {
      const hours = Math.floor(minutes / 60)
      const remainingMinutes = minutes % 60
      if (remainingMinutes === 0) {
        return `${hours} hour${hours > 1 ? 's' : ''}`
      } else {
        return `${hours} hour${
          hours > 1 ? 's' : ''
        } ${remainingMinutes} minute${remainingMinutes !== 1 ? 's' : ''}`
      }
    }
  }

  useEffect(() => {
    if (selectedDate) {
      const selectedDateStr = selectedDate.format('YYYY-MM-DD')

      const filtered = DeemedlogData?.filter((log) => {
        const logDateStr = new Date(log.logDate).toISOString().split('T')[0]
        return selectedDateStr === logDateStr
      })

      setFilteredLogs(filtered)
    } else {
      setFilteredLogs(DeemedlogData)
    }
  }, [selectedDate, DeemedlogData])

  // Show alert helper function
  const showAlert = (message, severity = 'success') => {
    setAlertMessage(message)
    setAlertSeverity(severity)
    setAlertOpen(true)
  }

  // Handle alert close
  const handleAlertClose = (event, reason) => {
    if (reason === 'clickaway') {
      return
    }
    setAlertOpen(false)
  }

  // Handle delete dialog open
  const handleDeleteClick = (logData) => {
    setLogToDelete(logData)
    setDeleteDialogOpen(true)
  }

  // Handle delete dialog close
  const handleCloseDelete = () => {
    setDeleteDialogOpen(false)
    setLogToDelete(null)
  }

  // Handle actual delete
  const handleConfirmDelete = async () => {
    if (!logToDelete) return

    try {
      const response = await DeleteLogDta(logToDelete.logId, token)
      if (response.status === 204) {
        showAlert('Log deleted successfully.', 'success')
        refreshLogs()
      } else {
        showAlert('Failed to delete log', 'error')
        console.error(response)
      }
    } catch (error) {
      showAlert('Error deleting log', 'error')
      console.error('Delete error:', error)
    } finally {
      handleCloseDelete()
    }
  }

  const handleEdit = (logData) => {
    if (onEditLog) {
      onEditLog(logData)
    }
  }

  const exportToCSV = () => {
    const headings = [
      'Date',
      'Duration',
      'Total Duration',
      'Fault Type',
      'Energy Loss',
      'Multiplying Factor',
      'Description',
      'Accountability',
      'Deemed Generated',
    ]
    const csvContent = [headings.join(',')]

    //exports filtered data only
    const dataToExport = filteredLogs.length > 0 ? filteredLogs : DeemedlogData

    dataToExport?.forEach((log) => {
      const energyLoss = Number(log?.energyLoss || 0)
      const MFactor = Number(log?.multiplyingFactor || 1)
      const duration = log?.duration || ''
      const timePeriodCount = duration ? duration.split('|').length : 1
      const deemedEnergy = energyLoss * MFactor * timePeriodCount
      const totalDurationMinutes = calculateTotalDuration(log?.duration)

      // Handle duration display for CSV
      let durationDisplay = ''
      if (duration.includes(' | ')) {
        // Multiple periods
        const periods = duration.split(' | ')
        durationDisplay = periods
          .map((period) => {
            const [start, end] = period.split(' : ')
            return `${formatTime(start?.trim())} To ${formatTime(end?.trim())}`
          })
          .join(' | ')
      } else {
        // Single period
        const [start, end] = duration.split(' : ')
        durationDisplay = `${formatTime(start?.trim())} To ${formatTime(
          end?.trim()
        )}`
      }

      const rowData = [
        formatLogDate(log?.logDate),
        `"${durationDisplay}"`,
        `"${formatDuration(totalDurationMinutes)}"`,
        `"${log?.faultType || ''}"`,
        log?.energyLoss || 0,
        log?.multiplyingFactor || 'N/A',
        `"${log?.description || ''}"`,
        `"${log?.accountability || 'N/A'}"`,
        deemedEnergy.toFixed(2) + ' kW',
      ]
      csvContent.push(rowData.join(','))
    })

    const blob = new Blob([csvContent.join('\n')], {
      type: 'text/csv;charset=utf-8',
    })

    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'deemed_generation_logs.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    showAlert('CSV exported successfully!', 'success')
  }

  const calculateDeemedEnergy = (log) => {
    const energyLoss = Number(log?.energyLoss || 0)
    const MFactor = Number(log?.multiplyingFactor || 1)
    const duration = log?.duration || ''
    const timePeriodCount = duration ? duration.split('|').length : 1
    return (energyLoss * MFactor).toFixed(2)
  }

  return (
    <div className={classes.root}>
      <Box display="flex" gap={2} justifyContent="flex-end" mb={2}>
        <Box>
          <Button
            onClick={exportToCSV}
            sx={{
              borderRadius: '12px',
              textTransform: 'none',
            }}
          >
            Download as CSV
          </Button>
        </Box>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Filter by Date"
            value={selectedDate}
            onChange={(newValue) => setSelectedDate(newValue)}
            format="DD-MM-YYYY"
            slotProps={{
              textField: {
                variant: 'outlined',
                size: 'small',
                sx: {
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '24px',
                  },
                },
              },
            }}
          />
        </LocalizationProvider>
        {selectedDate && (
          <IconButton
            onClick={() => setSelectedDate(null)}
            color="primary"
            title="Clear filter"
            sx={{
              bgcolor: 'primary.light',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.main',
              },
            }}
          >
            <Clear />
          </IconButton>
        )}
      </Box>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell
              align="center"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              Date
            </TableCell>
            <TableCell
              align="center"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              Duration
            </TableCell>
            <TableCell
              align="center"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              Total Duration
            </TableCell>
            <TableCell
              align="center"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              Fault Type
            </TableCell>
            <TableCell
              align="center"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              Energy loss
            </TableCell>
            <TableCell
              align="center"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              Multiplying Factor
            </TableCell>
            <TableCell
              align="center"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              Description
            </TableCell>
            <TableCell
              align="center"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              Accountability
            </TableCell>
            <TableCell
              align="center"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              Deemed Generated
            </TableCell>
            <TableCell
              align="center"
              style={{ fontSize: '14px', fontWeight: 'bold' }}
            >
              Actions
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {filteredLogs?.length > 0 ? (
            filteredLogs.map((Data, index) => (
              <TableRow key={Data?.logId || index}>
                <TableCell align="center">
                  {formatLogDate(Data?.logDate)}
                </TableCell>
                <TableCell align="center">
                  {Data?.duration?.includes(' | ')
                    ? // Multiple periods case
                      Data.duration.split(' | ').map((period, i) => (
                        <div key={i}>
                          {(() => {
                            const [start, end] = period.split(' : ')
                            return `${formatTime(
                              start?.trim()
                            )} To ${formatTime(end?.trim())}`
                          })()}
                          {i < Data.duration.split(' | ').length - 1 && <br />}
                        </div>
                      ))
                    : // Single period case
                      (() => {
                        const [start, end] = (Data?.duration || '').split(' : ')
                        return `${formatTime(start?.trim())} To ${formatTime(
                          end?.trim()
                        )}`
                      })()}
                </TableCell>
                <TableCell align="center">
                  {formatDuration(calculateTotalDuration(Data?.duration))}
                </TableCell>
                <TableCell align="center">{Data?.faultType || 'N/A'}</TableCell>
                <TableCell align="center">{Data?.energyLoss || 0}</TableCell>
                <TableCell align="center">
                  {Data?.multiplyingFactor || 'N/A'}
                </TableCell>
                <TableCell align="center">
                  {Data?.description || 'N/A'}
                </TableCell>
                <TableCell align="center">
                  {Data?.accountability || 'N/A'}
                </TableCell>
                <TableCell align="center">
                  {calculateDeemedEnergy(Data)} kW
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    aria-label="edit"
                    onClick={() => handleEdit(Data)}
                    color="primary"
                    sx={{ mr: 1 }}
                    title="Edit Log"
                  >
                    <Edit />
                  </IconButton>
                  <IconButton
                    aria-label="delete"
                    onClick={() => handleDeleteClick(Data)}
                    color="error"
                    title="Delete Log"
                  >
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={10}
                align="center"
                style={{ padding: '20px' }}
              >
                {selectedDate
                  ? 'No logs found for the selected date.'
                  : 'No logs available.'}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Delete Deemed Generation Log
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this deemed generation log?.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Custom Alert */}
      <CustomAlert
        open={alertOpen}
        handleClose={handleAlertClose}
        message={alertMessage}
        severity={alertSeverity}
      />
    </div>
  )
}

DeemedReport.propTypes = {}

export default DeemedReport
